import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { getBrand } from "@/lib/api";
import { CartProvider } from "@/lib/cart";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AgeGate from "@/components/AgeGate";
import StickyCTA from "@/components/StickyCTA";
import CartDrawer from "@/components/CartDrawer";
import LocationPickerModal from "@/components/LocationPickerModal";

// Lazy: ChatWidget pulls in the Claude session + a heavy panel UI. Defer
// its bundle until the user actually opens it — saves ~80kb gz off the
// initial paint. The wrapper below keeps the floating toggle visible
// immediately by rendering a placeholder until the chunk loads.
const ChatWidget = lazy(() => import("@/components/ChatWidget"));

import Home from "@/pages/Home";
import LocationsList from "@/pages/LocationsList";
import LocationDetail from "@/pages/LocationDetail";
import CityPage from "@/pages/CityPage";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import Sitemap from "@/pages/Sitemap";
import StoreMenu from "@/pages/StoreMenu";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import SpecialDetail from "@/pages/SpecialDetail";
import BrandDetail from "@/pages/BrandDetail";
import BrandsDirectory from "@/pages/BrandsDirectory";
import Loyalty, { CheckPointsModal } from "@/pages/Loyalty";
import StoreHome from "@/pages/StoreHome";
import StoreContact from "@/pages/StoreContact";
import Community from "@/pages/Community";
import Delivery from "@/pages/Delivery";
import SpecialsDirectory from "@/pages/SpecialsDirectory";
import Events from "@/pages/Events";
import Learn from "@/pages/Learn";
import Strains from "@/pages/Strains";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Accessibility from "@/pages/Accessibility";
import WhyZenZest from "@/pages/WhyZenZest";
import StrainLibrary, { StrainDetail } from "@/pages/StrainLibrary";
import { LoyaltyProvider, useLoyalty } from "@/lib/loyalty";
const AdminInsights = lazy(() => import("@/pages/AdminInsights"));
const NeighborhoodGuide = lazy(() => import("@/pages/NeighborhoodGuide"));
const NeighborhoodDirectory = lazy(() => import("@/pages/NeighborhoodDirectory"));
const CategoryLocationGuide = lazy(() => import("@/pages/CategoryLocationGuide"));
const FaqHub = lazy(() => import("@/pages/FaqHub"));
const FaqTopic = lazy(() => import("@/pages/FaqTopic"));
const FaqQuestion = lazy(() => import("@/pages/FaqQuestion"));
const BoroughHub = lazy(() => import("@/pages/BoroughHub"));
const ComparisonHub = lazy(() => import("@/pages/ComparisonHub"));
const ComparisonPage = lazy(() => import("@/pages/ComparisonPage"));
const VisitQueens = lazy(() => import("@/pages/VisitQueens"));

function GlobalLoyaltyModal() {
    const { open, closeModal } = useLoyalty();
    return <CheckPointsModal open={open} onClose={closeModal} />;
}

function App() {
    const [brand, setBrand] = useState(null);

    useEffect(() => {
        getBrand()
            .then(setBrand)
            .catch(() => {});
    }, []);

    return (
        <HelmetProvider>
            <BrowserRouter>
                <LoyaltyProvider>
                    <CartProvider>
                        <div className="App relative min-h-screen flex flex-col bg-zen-bg text-zen-ink">
                            <div className="zen-grain" aria-hidden="true" />
                            <AgeGate brand={brand} />
                            <Header brand={brand} />
                            <GlobalLoyaltyModal />
                            <main className="flex-1 relative">
                            <Routes>
                                <Route path="/" element={<Home brand={brand} />} />
                                <Route
                                    path="/locations"
                                    element={<LocationsList />}
                                />
                                <Route
                                    path="/locations/:slug"
                                    element={<LocationDetail brand={brand} />}
                                />
                                <Route
                                    path="/dispensary-near/:slug"
                                    element={<CityPage brand={brand} />}
                                />
                                <Route path="/blog" element={<BlogList />} />
                                <Route
                                    path="/blog/:slug"
                                    element={<BlogPost brand={brand} />}
                                />
                                <Route path="/sitemap" element={<Sitemap />} />
                                <Route
                                    path="/menu/:storeSlug"
                                    element={<StoreMenu />}
                                />
                                <Route
                                    path="/brands/:storeSlug"
                                    element={<BrandsDirectory />}
                                />
                                <Route path="/loyalty" element={<Loyalty />} />
                                <Route path="/visit/queens" element={<VisitQueens />} />
                                <Route path="/queens/loyalty" element={<Loyalty />} />
                                <Route path="/staten-island/loyalty" element={<Loyalty />} />

                                {/* Community support (Childhelp partnership) */}
                                <Route path="/community" element={<Community />} />
                                <Route path="/community-support" element={<Community />} />
                                <Route path="/queens/community" element={<Community />} />
                                <Route path="/queens/community-support" element={<Community />} />
                                <Route path="/staten-island/community" element={<Community />} />
                                <Route path="/staten-island/community-support" element={<Community />} />

                                {/* Specials directory + detail */}
                                <Route path="/specials" element={<SpecialsDirectory />} />
                                <Route
                                    path="/menu/:storeSlug/specials"
                                    element={<SpecialsDirectory />}
                                />

                                {/* Static brief pages */}
                                <Route path="/events" element={<Events />} />
                                <Route path="/learn" element={<Learn />} />
                                <Route path="/strains" element={<Strains />} />
                                <Route path="/strains/:slug" element={<StrainLibrary />} />
                                <Route path="/menu/:storeSlug/strain/:strainSlug" element={<StrainDetail />} />
                                <Route path="/privacy" element={<Privacy />} />
                                <Route path="/terms" element={<Terms />} />
                                <Route path="/accessibility" element={<Accessibility />} />
                                <Route path="/why-zenzest" element={<WhyZenZest />} />

                                {/* Same-day delivery */}
                                <Route path="/delivery" element={<Delivery />} />
                                <Route path="/cannabis-delivery" element={<Delivery />} />
                                <Route path="/weed-delivery" element={<Delivery />} />
                                <Route path="/queens/cannabis-delivery" element={<Delivery />} />
                                <Route path="/queens/delivery" element={<Delivery />} />
                                <Route path="/staten-island/cannabis-delivery" element={<Delivery />} />
                                <Route path="/staten-island/delivery" element={<Delivery />} />

                                {/* ZenZest store-prefixed routes (per brief). Each store's
                                    sub-pages alias to the existing directory routes so we
                                    don't duplicate menus, deals, blog, or location info. */}
                                <Route path="/queens" element={<StoreHome />} />
                                <Route path="/staten-island" element={<StoreHome />} />
                                <Route path="/queens/contact" element={<StoreContact />} />
                                <Route path="/staten-island/contact" element={<StoreContact />} />
                                <Route
                                    path="/queens/locations"
                                    element={<Navigate to="/locations/queens-long-island" replace />}
                                />
                                <Route
                                    path="/staten-island/locations"
                                    element={<Navigate to="/locations/staten-island" replace />}
                                />
                                <Route
                                    path="/queens/deals"
                                    element={<Navigate to="/menu/queens-long-island" replace />}
                                />
                                <Route
                                    path="/staten-island/deals"
                                    element={<Navigate to="/menu/staten-island" replace />}
                                />
                                <Route
                                    path="/queens/blog"
                                    element={<Navigate to="/blog" replace />}
                                />
                                <Route
                                    path="/staten-island/blog"
                                    element={<Navigate to="/blog" replace />}
                                />
                                <Route
                                    path="/menu/:storeSlug/special/:specialId"
                                    element={<SpecialDetail />}
                                />
                                <Route
                                    path="/menu/:storeSlug/brand/:brandSlug"
                                    element={<BrandDetail />}
                                />
                                <Route
                                    path="/menu/:storeSlug/:productSlug"
                                    element={<ProductDetail />}
                                />
                                <Route
                                    path="/checkout/:storeSlug"
                                    element={<Checkout />}
                                />
                                <Route
                                    path="/admin/insights"
                                    element={
                                        <Suspense fallback={null}>
                                            <AdminInsights />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/dispensary"
                                    element={
                                        <Suspense fallback={null}>
                                            <NeighborhoodDirectory />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/dispensary/borough/:slug"
                                    element={
                                        <Suspense fallback={null}>
                                            <BoroughHub />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/dispensary/:slug"
                                    element={
                                        <Suspense fallback={null}>
                                            <NeighborhoodGuide />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/dispensary/:slug/:catSlug"
                                    element={
                                        <Suspense fallback={null}>
                                            <CategoryLocationGuide />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/faq"
                                    element={
                                        <Suspense fallback={null}>
                                            <FaqHub />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/faq/topic/:slug"
                                    element={
                                        <Suspense fallback={null}>
                                            <FaqTopic />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/faq/q/:slug"
                                    element={
                                        <Suspense fallback={null}>
                                            <FaqQuestion />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/compare/zenzest-vs-other-dispensary"
                                    element={
                                        <Suspense fallback={null}>
                                            <ComparisonHub />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/compare/zenzest-vs-other-dispensary/:slug"
                                    element={
                                        <Suspense fallback={null}>
                                            <ComparisonPage />
                                        </Suspense>
                                    }
                                />
                            </Routes>
                        </main>
                        <Footer brand={brand} />
                        <StickyCTA brand={brand} />
                        <CartDrawer />
                        <Suspense fallback={null}>
                            <ChatWidget />
                        </Suspense>
                        <LocationPickerModal />
                        </div>
                    </CartProvider>
                </LoyaltyProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
